/**
 * Friends — roster + admin panel (INVENTORY §1 screen 9, §4.7, §3; prototype
 * template lines 613-694). Reached from Profile ▸ FRIENDS, not the bottom nav.
 *
 * No props: every value comes from `crewVM()` and every write is a crew-slice
 * action. Two behaviours are worth spelling out because they look like screen
 * state and are not:
 *
 *  - **REMOVE is two-tap.** `rmArm` lives in the ui slice, so navigating away
 *    disarms it (`nav()` clears it) and the armed label/colour arrive
 *    pre-resolved as `rmTxt` / `rmBd` / `rmClr`.
 *  - **There is no role control.** Roles follow who owns the crew (D4), the
 *    design removed the select with CO-ADMIN, and phase B retired the endpoint
 *    behind it — so the row shows a read-only OWNER / PLAYER chip. Changing who
 *    owns a crew is TRANSFER OWNERSHIP, not a dropdown.
 *
 * DEVIATIONS
 *  - D-03: `COPY INVITE` copies identity only. The row says so.
 *  - P2-08 is CLOSED (migration 3010). The dots are real for the whole crew now,
 *    where they used to read LINKED on your card and NOT LINKED on everyone
 *    else's regardless of the truth. The prototype hardcoded
 *    `['marcus','chris','chelsea']`; this asks. The chip blanks while the RPC is
 *    in flight rather than guessing — see the `u.stTxt` guard below.
 */

import { useEffect } from 'react';

import { Avatar } from '../components/Avatar.tsx';
import { useQd } from '../store/index.ts';
import { crewVM } from '../store/selectors.ts';

export default function Crew(): JSX.Element {
  const s = useQd();
  const v = crewVM(s);

  useEffect(() => {
    if (v.meAdmin) {
      void s.loadPending();
      // 3056: the join link and its requests load on the same trigger and the
      // same owner check — three reads for one screen, all of which swallow
      // their own failures rather than blanking the section.
      void s.loadJoinLink();
      void s.loadJoinRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v.meAdmin]);

  return (
    <div
      data-screen-label="Party"
      style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px 14px 26px' }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ fontFamily: 'var(--gt-font-pixel)', fontSize: '11px', color: 'var(--gt-text)' }}>PARTY</div>
        <div
          style={{
            fontFamily: 'var(--gt-font-mono)',
            fontSize: '9.5px',
            lineHeight: 1.7,
            color: 'var(--gt-muted)',
          }}
        >
          <span>{v.crewSub}</span>
        </div>
      </div>

      {/* MANAGE PARTY (AA phase 5 §K), leaders only — 48px. */}
      {v.meAdmin ? (
        <div
          onClick={() => s.nav('manage')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            height: 48,
            padding: '0 14px',
            border: '1px solid var(--gt-border)',
            background: 'var(--gt-card)',
            cursor: 'pointer',
            boxSizing: 'border-box',
          }}
        >
          <span style={{ fontFamily: 'var(--gt-font-mono)', fontSize: 12, color: 'var(--acc)' }}>◈</span>
          <span style={{ fontFamily: 'var(--gt-font-pixel)', fontSize: 8.5, color: 'var(--gt-text)', transform: 'translateY(0.13em)' }}>MANAGE PARTY</span>
          {v.hasJqWait ? (
            <span style={{ fontFamily: 'var(--gt-font-mono)', fontSize: 8, letterSpacing: '.08em', color: 'var(--acc)', border: '1px solid var(--acc)', padding: '3px 6px' }}>{v.jqWaitTxt}</span>
          ) : null}
          <div style={{ flex: 1 }} />
          <span style={{ fontFamily: 'var(--gt-font-mono)', fontSize: 11, color: 'var(--gt-text-5)' }}>▸</span>
        </div>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {v.crewCards.map((u) => (
          <div
            key={u.key}
            onClick={() => s.openMember(u.k)}
            style={{
              cursor: 'pointer',
              display: 'flex',
              gap: '13px',
              alignItems: 'center',
              border: '1px solid var(--gt-line)',
              background: 'var(--gt-card)',
              padding: '12px',
            }}
          >
            <Avatar size={48} fs={12} ini={u.ini} bg={u.hueBg} hasAv={u.hasAv} avBg={u.avBg} />
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <div style={{ display: 'flex', gap: '7px', alignItems: 'baseline' }}>
                <span style={{ fontWeight: 600, fontSize: '13.5px', color: 'var(--gt-text)' }}>{u.name}</span>
                {u.isYou ? (
                  <span
                    style={{
                      fontFamily: 'var(--gt-font-pixel)',
                      fontSize: '6px',
                      background: 'var(--gt-text)',
                      color: 'var(--gt-ink)',
                      padding: '3px 4px',
                    }}
                  >
                    YOU
                  </span>
                ) : null}
              </div>
              <div style={{ fontFamily: 'var(--gt-font-mono)', fontSize: '9px', color: u.roleClr }}>
                <span>{u.roleTxt}</span>
                {u.isTemp ? <span style={{ color: 'var(--acc2)' }}> · TEMP PASS</span> : null}
              </div>
              <div style={{ fontFamily: 'var(--gt-font-mono)', fontSize: '9px', color: 'var(--gt-muted-2)' }}>
                <span>{u.statsTxt}</span>
              </div>
              {/* Blank until `crew_steam_links` answers, exactly as the detail
                  panel's library column blanks until the ownership RPC does —
                  an unanswered question must never paint as an answer. Your own
                  card never waits: `crewVM` reads it off the boot-time link. */}
              {u.stTxt ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ width: '6px', height: '6px', flex: 'none', background: u.stDot }} />
                  <span
                    style={{
                      fontFamily: 'var(--gt-font-mono)',
                      fontSize: '8.5px',
                      letterSpacing: '.03em',
                      color: u.stClr,
                    }}
                  >
                    {u.stTxt}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      {v.notAdmin ? (
        <div
          style={{
            border: '1px dashed var(--gt-border-2)',
            padding: '12px 14px',
            fontFamily: 'var(--gt-font-mono)',
            fontSize: '10px',
            lineHeight: 1.7,
            color: 'var(--gt-muted)',
          }}
        >
          {v.askAdmTxt}
        </div>
      ) : null}

    </div>
  );
}

